import { Fragment, useEffect, useRef, useState } from "react"
import Api from "../api/api"
import { Sku, SkuVersion, SkuVersionStatus } from "../api/models"
import Spinner from "./widgets/Spinner"
import { backdropDismiss } from "../utils/backdropDismiss"

// The "All versions" dialog for ONE sku: every release (plus any test
// version the user holds), newest first, lazily fetched on open, each with
// BOTH platforms' installer links. The card's main links stay the one-click
// way to the latest version for the CURRENT platform — this dialog is where
// the other platform (and history) is downloaded.
export default function AllVersionsDialog({ sku, accessToken, onClose }: {
  sku: Sku
  accessToken: string
  onClose: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [versions, setVersions] = useState<SkuVersion[] | undefined>(undefined)
  const [error, setError] = useState(false)

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  useEffect(() => {
    let stale = false
    Api.listMySkuVersions({ skuId: sku.id, accessToken }).then(({ versions }) => {
      if (!stale) setVersions(versions)
    }).catch(() => {
      if (!stale) setError(true)
    })
    return () => { stale = true }
  }, [sku.id, accessToken])

  const latestReleasedId = versions?.find(v => v.status === SkuVersionStatus.Released)?.id

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content no-scrollbar" style={{ maxHeight: "85vh", overflowY: "auto" }}>
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">{sku.name} versions</h2>

        {error ? (
          <p className="generate-codes-error">Could not load the version list.</p>
        ) : versions === undefined ? (
          <p className="empty-state"><Spinner size={16} /> Loading versions…</p>
        ) : versions.length === 0 ? (
          <p className="empty-state">No versions are available.</p>
        ) : (
          // one grid so the columns line up across every row:
          // version | date | spacer | mac | win
          <div style={{
            display: "grid",
            gridTemplateColumns: "max-content max-content 1fr max-content max-content",
            columnGap: 12, rowGap: 6, alignItems: "center",
            fontSize: "10pt", marginBottom: 12
          }}>
            {versions.map((version) => (
              <Fragment key={version.id}>
                <span>
                  v{version.version}
                  {version.status !== SkuVersionStatus.Released ? ` (${version.status})`
                    : version.id === latestReleasedId ? " (latest)" : ""}
                </span>
                <span style={{ color: "var(--nfa-light-grey)" }}>
                  {new Date(version.createdAt).toLocaleDateString()}
                </span>
                <span />
                <span>{version.downloadMacUrl && <a className="card-link" href={version.downloadMacUrl}>macOS</a>}</span>
                <span>{version.downloadWinUrl && <a className="card-link" href={version.downloadWinUrl}>Windows</a>}</span>
              </Fragment>
            ))}
          </div>
        )}

        <div className="redeem-actions">
          <button onClick={() => dialogRef.current?.close()}>Close</button>
        </div>
      </div>
    </dialog>
  )
}
